import { useState, useCallback, useMemo } from 'react'
import { Dialog, DialogBody, DialogActions } from '@/components/catalyst-ui/dialog'
import { XMarkIcon } from '@heroicons/react/24/solid'
import { usePageBlueprint } from '@/features/site-pages/hooks/usePageSettings'
import { useAiCompanionMode } from '@/features/site-pages/hooks/useAiCompanionMode'
import { useSelectionStore } from '@/state/selection.store'
import { useSubdomainEditor } from '../hooks/useSubdomainEditor'
import { useProjectPublishInfo } from '../hooks/useProjectPublishInfo'
import { usePublishFlow } from '../hooks/usePublishFlow'
import { useExportFlow } from '../hooks/useExportFlow'
import { useNavigation } from '../hooks/useNavigation'
import { LivePublishTab } from '@/features/publish/components/LivePublishTab'
import { ExportCodeTab } from '@/features/publish/components/ExportCodeTab'
import { SeatbeltAnimation } from '@/features/publish/components/SeatbeltAnimation'
import { useListUserDomains, useWorkspaceDomainsWithNs } from '@/features/domains/hooks/useMyDomains'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useActiveWorkspace } from '@/state/selection.store'

const getSearchParam = (key) => {
  return typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get(key) : null
}

export function PublishDialog({ isOpen, onClose, onPublishSuccess, projectName, publishedHref, uniqueUrl: propUniqueUrl }) {
  const [activeTab, setActiveTab] = useState('live')
  const currentProject = useSelectionStore(state => state.currentProject)
  const projects = useSelectionStore(state => state.projects)
  const { user } = useAuth()
  const { workspaceId } = useActiveWorkspace()

  const uniqueUrl = useMemo(() => {
    return propUniqueUrl
      || currentProject?.uniqueUrl
      || projects?.find(p => (p.id || p._id) === getSearchParam('project'))?.uniqueUrl
      || null
  }, [propUniqueUrl, currentProject, projects])

  // Get project ID for domain lookup
  const projectId = useMemo(() => {
    const projectIdFromUrl = getSearchParam('project')
    return projectIdFromUrl || currentProject?.id || currentProject?._id || null
  }, [currentProject])

  const { data: projectData } = usePageBlueprint(uniqueUrl)
  const { isAiCompanionMode } = useAiCompanionMode(uniqueUrl)
  const { navigateToPage } = useNavigation()

  // Fetch third-party domains for this workspace
  const { data: userDomainsData } = useListUserDomains(user?.id, {
    workspaceId,
    offset: 0,
    limit: 100
  }, { enabled: !!user?.id && !!workspaceId })

  // Fetch domains with NS for this workspace
  const { data: domainsWithNsData } = useWorkspaceDomainsWithNs(workspaceId, {
    enabled: !!workspaceId
  })

  // Find third-party domain connected to this project
  const connectedThirdPartyDomain = useMemo(() => {
    if (!userDomainsData?.data || !projectId) return null
    return userDomainsData.data.find(d => {
      const domainProjectId = typeof d.project === 'string' ? d.project : (d.project?._id || d.project?.id)
      return domainProjectId && String(domainProjectId) === String(projectId)
    })
  }, [userDomainsData, projectId])

  // Find domain with NS connected to this project
  const connectedDomainWithNs = useMemo(() => {
    if (!domainsWithNsData?.data || !projectId) return null
    return domainsWithNsData.data.find(d => {
      const domainProjectId = d.project_id || d.project || (typeof d.project === 'object' ? (d.project?._id || d.project?.id) : null)
      return domainProjectId && String(domainProjectId) === String(projectId)
    })
  }, [domainsWithNsData, projectId])

  // Use whichever domain is connected (third-party OR with-NS)
  const connectedDomain = connectedThirdPartyDomain || connectedDomainWithNs

  const publishInfo = useProjectPublishInfo(projectData, isAiCompanionMode, publishedHref, connectedDomain)
  const subdomainEditor = useSubdomainEditor(publishInfo.projectSubdomain, isOpen)
  const { isPublishing, publishState, publish, startPublishing, handleSuccessDone } = usePublishFlow(uniqueUrl, onClose, onPublishSuccess)
  const exportFlow = useExportFlow(uniqueUrl, projectName, onClose, isAiCompanionMode)

  const handlePublish = useCallback(() => {
    publish({
      isDomainConnected: publishInfo.isDomainConnected,
      isWordpressConnected: publishInfo.isWordpressConnected,
      isFirstTimePublish: publishInfo.isFirstTimePublish,
      isSubdomainActive: publishInfo.isSubdomainActive,
      projectSubdomain: publishInfo.projectSubdomain,
      subdomainEditor: subdomainEditor.editor,
      subdomainValue: subdomainEditor.value,
      subdomainValidation: subdomainEditor.validation,
      isCheckingSubdomain: subdomainEditor.isChecking,
    })
  }, [publish, publishInfo, subdomainEditor])

  const handleClose = useCallback(() => {
    if (publishState === 'success') {
      handleSuccessDone()
    } else {
      subdomainEditor.handleCancel()
      onClose?.()
    }
  }, [subdomainEditor, onClose, publishState, handleSuccessDone])

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      size="xl"
      className="!rounded-xl !bg-[#FAFAFA] !shadow-[0px_18px_45px_0px_#00000026] !p-0 !max-w-[380px] sm:!fixed !top-18 !right-3 !mt-0"
    >
      <DialogBody className="!p-0 relative !mt-4">
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-3 rounded-full !text-black hover:!text-[#52525B] cursor-pointer data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2 focus:outline-none"
        >
          <XMarkIcon className="!w-5 !h-5 -mt-2 -mr-1" />
        </button>

        {/* Seatbelt animation - smooth transition in/out */}
        <div
          className="overflow-hidden transition-all duration-500 ease-in-out"
          style={{ maxHeight: publishState === 'publish-animation' ? '120px' : '0px', opacity: publishState === 'publish-animation' ? 1 : 0 }}
        >
          <SeatbeltAnimation
            onComplete={startPublishing}
            shouldAnimate={publishState === 'publish-animation'}
          />
        </div>

        <div className="border-b border-[#E4E4E7] flex justify-start">
          <button
            type="button"
            disabled={publishState === 'publish-animation' || publishState === 'publishing'}
            onClick={() => setActiveTab('live')}
            className={`px-4 pb-3 text-sm font-semibold focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2 ${activeTab === 'live' ? 'text-[#18181B] border-b-2 border-[#000000]' : 'text-[#A1A1AA]'
              }`}
          >
            Live Publish
          </button>
          <button
            type="button"
            disabled={publishState === 'publish-animation' || publishState === 'publishing'}
            onClick={() => setActiveTab('export')}
            className={`px-4 pb-3 text-sm font-semibold text-center focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2 ${activeTab === 'export' ? 'text-[#18181B] border-b-2 border-[#000000]' : 'text-[#A1A1AA]'
              }`}
          >
            Export Code
          </button>
        </div>

        {activeTab === 'live' ? (
          <LivePublishTab
            publishInfo={publishInfo}
            subdomainEditor={subdomainEditor}
            isPublishing={isPublishing}
            publishState={publishState}
            onPublish={handlePublish}
            onSuccessDone={handleSuccessDone}
            onNavigateToPage={navigateToPage}
            onClose={handleClose}
            isAiCompanionMode={isAiCompanionMode}
          />
        ) : (
          <ExportCodeTab
            exportFlow={exportFlow}
            assetsCount={publishInfo.assetsCount}
            isAiCompanionMode={isAiCompanionMode}
          />
        )}
      </DialogBody>
      <DialogActions className="hidden" />
    </Dialog>
  )
}
